import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { toast } from 'sonner';
import { CheckCircle, ArrowLeft, UploadSimple, File as FileIcon, X } from '@phosphor-icons/react';

const CustomInquiry = () => {
  const [mode, setMode] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [customizeForm, setCustomizeForm] = useState({
    name: '', email: '', phone: '',
    color: '', size: '', texture: '', notes: ''
  });

  const [ownForm, setOwnForm] = useState({
    name: '', email: '', phone: '', requirements: ''
  });

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'pdf'].includes(ext)) {
      toast.error('Only JPEG, PNG, and PDF files are allowed');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadedFile({ path: response.data.path, filename: file.name });
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleCustomizeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const requirements = `Customizing: ${selectedProduct.name}\nColor: ${customizeForm.color}\nSize: ${customizeForm.size}\nTexture: ${customizeForm.texture}\nNotes: ${customizeForm.notes}`;
      await api.post('/inquiries/custom', {
        name: customizeForm.name,
        email: customizeForm.email,
        phone: customizeForm.phone,
        requirements
      });
      setSubmitted(true);
      toast.success('Inquiry submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit inquiry');
    } finally {
      setLoading(false);
    }
  };

  const handleOwnSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const requirements = uploadedFile
        ? `${ownForm.requirements}\n\nDesign file: ${uploadedFile.filename} (${uploadedFile.path})`
        : ownForm.requirements;
      await api.post('/inquiries/custom', {
        name: ownForm.name,
        email: ownForm.email,
        phone: ownForm.phone,
        requirements
      });
      setSubmitted(true);
      toast.success('Inquiry submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit inquiry');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="custom-inquiry-success" className="min-h-screen pt-24 pb-16 bg-[#F4F4F4] flex items-center justify-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md px-6">
          <CheckCircle size={64} weight="thin" className="text-green-600 mx-auto mb-4" />
          <h2 className="text-3xl font-light mb-4">Thank You!</h2>
          <p className="text-[#7a5e75] mb-8">Your custom inquiry has been received. Our team will contact you within 24 hours.</p>
          <button onClick={() => { setSubmitted(false); setMode(null); setSelectedProduct(null); setUploadedFile(null); }} data-testid="submit-another-inquiry-button" className="btn-secondary">
            Submit Another Inquiry
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="custom-inquiry-page" className="min-h-screen pt-24 pb-16 bg-[#F4F4F4]">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
          <h1 data-testid="custom-inquiry-heading" className="text-4xl md:text-5xl font-light tracking-tighter mb-4">
            Custom Order Inquiry
          </h1>
          <p className="text-base text-[#7a5e75] leading-relaxed">
            Choose how you'd like to create your custom planter.
          </p>
        </motion.div>

        {/* Step 1: Choose mode */}
        {!mode && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <button
              data-testid="customize-our-design-button"
              onClick={() => setMode('customize')}
              className="group text-left bg-white border border-[#E5E2DC] overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
            >
              <div className="h-80 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/2w28ryv4_ChatGPT%20Image%20Apr%2018%2C%202026%20at%2011_27_12%20AM.png"
                  alt="Customize our design"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-light text-[#641b4d] mb-2">Customize Our Design</h3>
                <p className="text-sm text-[#7a5e75]">Pick one of our existing planters and customize the color, size, or texture to your preference.</p>
              </div>
            </button>

            <button
              data-testid="share-my-design-button"
              onClick={() => setMode('own')}
              className="group text-left bg-white border border-[#E5E2DC] overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
            >
              <div className="h-80 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1626256223743-603107306ff2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxoYW5kJTIwc2tldGNoaW5nJTIwcHJvZHVjdCUyMGRlc2lnbiUyMG5vdGVib29rJTIwb3ZlcmhlYWR8ZW58MHx8fHwxNzc2NDkxNDU1fDA&ixlib=rb-4.1.0&q=85"
                  alt="Share your own design"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-light text-[#641b4d] mb-2">Share My Design</h3>
                <p className="text-sm text-[#7a5e75]">Have your own design in mind? Share your requirements and we'll bring it to life.</p>
              </div>
            </button>
          </motion.div>
        )}

        {/* Step 2a: Customize Our Design - Select Product */}
        {mode === 'customize' && !selectedProduct && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button data-testid="back-to-options" onClick={() => setMode(null)} className="flex items-center gap-2 text-sm text-[#7a5e75] hover:text-[#641b4d] mb-8 transition-colors">
              <ArrowLeft size={20} /> Back to options
            </button>
            <h2 className="text-2xl font-light mb-8">Select a design to customize</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map(product => (
                <button key={product.id} data-testid={`select-product-${product.id}`} onClick={() => setSelectedProduct(product)} className="group text-left bg-white border border-[#E5E2DC] overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                  <div className="overflow-hidden aspect-square bg-[#F4F4F4]">
                    <img src={product.image_url} alt={product.name} className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-light text-[#641b4d]">{product.name}</h3>
                    <p className="text-xs text-[#7a5e75] mt-1">{product.size}</p>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Step 2b: Customize Our Design - Customization Form */}
        {mode === 'customize' && selectedProduct && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button data-testid="back-to-products" onClick={() => setSelectedProduct(null)} className="flex items-center gap-2 text-sm text-[#7a5e75] hover:text-[#641b4d] mb-8 transition-colors">
              <ArrowLeft size={20} /> Back to designs
            </button>

            <div className="bg-white border border-[#E5E2DC] p-6 mb-6 flex gap-6 items-center">
              <img src={selectedProduct.image_url} alt={selectedProduct.name} className="w-24 h-24 object-contain bg-[#F4F4F4]" />
              <div>
                <h3 className="text-xl font-light mb-1">{selectedProduct.name}</h3>
                <p className="text-sm text-[#7a5e75]">{selectedProduct.size} | {selectedProduct.shape}</p>
              </div>
            </div>

            {/* Disclaimer */}
            <div data-testid="color-disclaimer" className="bg-[#bc64aa]/10 border border-[#bc64aa]/20 px-6 py-4 mb-8">
              <p className="text-sm text-[#bc64aa]">
                If the selected colour is out of stock after the order has been placed, we will issue an immediate refund.
              </p>
            </div>

            <div className="bg-white border border-[#E5E2DC] p-8">
              <h2 className="text-2xl font-light mb-6">Customize This Design</h2>
              <form onSubmit={handleCustomizeSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Full Name *</label>
                    <input type="text" data-testid="customize-name" value={customizeForm.name} onChange={e => setCustomizeForm({...customizeForm, name: e.target.value})} required className="input-underline w-full" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Email *</label>
                    <input type="email" data-testid="customize-email" value={customizeForm.email} onChange={e => setCustomizeForm({...customizeForm, email: e.target.value})} required className="input-underline w-full" />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Phone *</label>
                  <input type="tel" data-testid="customize-phone" value={customizeForm.phone} onChange={e => setCustomizeForm({...customizeForm, phone: e.target.value})} required className="input-underline w-full" />
                </div>

                <div className="border-t border-[#E5E2DC] pt-6">
                  <h3 className="text-lg font-light mb-4 text-[#641b4d]">What would you like to change?</h3>
                  <div className="space-y-6">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Color</label>
                      <input type="text" data-testid="customize-color" placeholder="e.g. Matte Black, Terracotta Red, Forest Green..." value={customizeForm.color} onChange={e => setCustomizeForm({...customizeForm, color: e.target.value})} className="input-underline w-full" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Size</label>
                      <input type="text" data-testid="customize-size" placeholder='e.g. Larger (48" H), Smaller (24" H), Custom dimensions...' value={customizeForm.size} onChange={e => setCustomizeForm({...customizeForm, size: e.target.value})} className="input-underline w-full" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Texture / Finish</label>
                      <input type="text" data-testid="customize-texture" placeholder="e.g. Glossy, Matte, Grainy, Smooth..." value={customizeForm.texture} onChange={e => setCustomizeForm({...customizeForm, texture: e.target.value})} className="input-underline w-full" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Additional Notes</label>
                      <textarea data-testid="customize-notes" rows={4} placeholder="Any other changes or special requirements..." value={customizeForm.notes} onChange={e => setCustomizeForm({...customizeForm, notes: e.target.value})} className="input-underline w-full resize-none" />
                    </div>
                  </div>
                </div>

                {/* Delivery note */}
                <div data-testid="delivery-note" className="bg-[#F4F4F4] border border-[#E5E2DC] px-6 py-4">
                  <p className="text-sm text-[#7a5e75]">
                    Custom orders require additional production time. The estimated delivery date will be confirmed once your order is reviewed by our team.
                  </p>
                </div>

                <button type="submit" data-testid="submit-customize-inquiry" disabled={loading} className="btn-primary w-full disabled:opacity-50">
                  {loading ? 'Submitting...' : 'Submit Inquiry'}
                </button>
              </form>
            </div>
          </motion.div>
        )}

        {/* Step 2c: Share My Own Design */}
        {mode === 'own' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button data-testid="back-to-options-own" onClick={() => setMode(null)} className="flex items-center gap-2 text-sm text-[#7a5e75] hover:text-[#641b4d] mb-8 transition-colors">
              <ArrowLeft size={20} /> Back to options
            </button>

            <div className="bg-white border border-[#E5E2DC] p-8">
              <h2 className="text-2xl font-light mb-6">Share Your Design</h2>
              <form onSubmit={handleOwnSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Full Name *</label>
                    <input type="text" data-testid="own-name" value={ownForm.name} onChange={e => setOwnForm({...ownForm, name: e.target.value})} required className="input-underline w-full" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Email *</label>
                    <input type="email" data-testid="own-email" value={ownForm.email} onChange={e => setOwnForm({...ownForm, email: e.target.value})} required className="input-underline w-full" />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Phone Number *</label>
                  <input type="tel" data-testid="own-phone" value={ownForm.phone} onChange={e => setOwnForm({...ownForm, phone: e.target.value})} required className="input-underline w-full" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Your Requirements *</label>
                  <textarea data-testid="own-requirements" value={ownForm.requirements} onChange={e => setOwnForm({...ownForm, requirements: e.target.value})} required rows={6} placeholder="Please describe your design (dimensions, shape, color, finish, quantity, etc.)" className="input-underline w-full resize-none" />
                </div>

                {/* File Upload */}
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-3 block">Upload Your Design</label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".jpg,.jpeg,.png,.pdf"
                    className="hidden"
                  />
                  {!uploadedFile ? (
                    <button
                      type="button"
                      data-testid="upload-design-button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="w-full border-2 border-dashed border-[#E5E2DC] hover:border-[#7a5e75] transition-colors py-8 flex flex-col items-center gap-3"
                    >
                      <UploadSimple size={32} weight="light" className="text-[#7a5e75]" />
                      <span className="text-sm text-[#7a5e75]">
                        {uploading ? 'Uploading...' : 'Click to upload JPEG, PNG, or PDF'}
                      </span>
                    </button>
                  ) : (
                    <div data-testid="uploaded-file-info" className="flex items-center justify-between bg-[#F4F4F4] border border-[#E5E2DC] px-6 py-4">
                      <div className="flex items-center gap-3">
                        <FileIcon size={24} weight="light" className="text-[#641b4d]" />
                        <span className="text-sm text-[#641b4d]">{uploadedFile.filename}</span>
                      </div>
                      <button type="button" onClick={() => { setUploadedFile(null); fileInputRef.current.value = ''; }} className="text-[#bc64aa] hover:text-[#8c1666]">
                        <X size={20} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Delivery note */}
                <div data-testid="delivery-note-own" className="bg-[#F4F4F4] border border-[#E5E2DC] px-6 py-4">
                  <p className="text-sm text-[#7a5e75]">
                    Custom orders require additional production time. The estimated delivery date will be confirmed once your order is reviewed by our team.
                  </p>
                </div>

                <button type="submit" data-testid="submit-own-inquiry" disabled={loading} className="btn-primary w-full disabled:opacity-50">
                  {loading ? 'Submitting...' : 'Submit Inquiry'}
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CustomInquiry;
